import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Input,
  Table,
  Avatar,
  Typography,
  Space,
  Tag,
  Button,
  Modal,
  Form,
  InputNumber,
  message,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useUsers } from '@/hooks/useUsers';
import { buildImageUrl } from '@zero/shared';
import request from '@/utils/request';

const { Title } = Typography;
const { Search } = Input;

interface UserRecord {
  id: number;
  avatarUrl?: string;
  nickname?: string;
  openId?: string;
  coinBalance?: number;
  lastLogin?: string;
  createdAt?: string;
}

const UserListPage: React.FC = () => {
  const navigate = useNavigate();
  const { users, loading, pagination, setSearch, setPage, refresh } = useUsers();

  const [topupUser, setTopupUser] = useState<UserRecord | null>(null);
  const [form] = Form.useForm<{ coins: number; note?: string }>();
  const [topupLoading, setTopupLoading] = useState(false);

  const onTopup = async () => {
    if (!topupUser) return;
    try {
      const values = await form.validateFields();
      setTopupLoading(true);
      await request.post(`/admin/consumers/${topupUser.id}/topup`, {
        coins: values.coins,
        note: values.note,
      });
      message.success(values.coins > 0 ? '已充值' : '已扣减');
      setTopupUser(null);
      form.resetFields();
      refresh();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setTopupLoading(false);
    }
  };

  const columns: ColumnsType<UserRecord> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Avatar',
      dataIndex: 'avatarUrl',
      key: 'avatar',
      width: 70,
      render: (url: string) => (
        <Avatar src={buildImageUrl(url, { w: 40, h: 40 })} icon={<UserOutlined />} size="small" />
      ),
    },
    {
      title: 'Nickname',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 150,
      render: (val: string) => val || '-',
    },
    {
      title: 'OpenID',
      dataIndex: 'openId',
      key: 'openId',
      width: 200,
      ellipsis: true,
      render: (val: string) => val || '-',
    },
    {
      title: '虚拟币',
      dataIndex: 'coinBalance',
      key: 'coinBalance',
      width: 110,
      render: (val: number | undefined) => (
        <Tag color="gold" style={{ margin: 0 }}>
          {(val ?? 0).toLocaleString()}
        </Tag>
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      width: 160,
      render: (val: string) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val: string) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_, row) => (
        <Button
          size="small"
          type="link"
          onClick={(e) => {
            e.stopPropagation();
            setTopupUser(row);
          }}
        >
          充值
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Users
        </Title>
        <Search
          placeholder="Search by nickname or openId"
          allowClear
          onSearch={(value) => setSearch(value)}
          style={{ width: 320 }}
        />
      </Space>

      <Table<UserRecord>
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} users`,
          onChange: (page, pageSize) => setPage(page, pageSize),
        }}
        onRow={(record) => ({
          onClick: () => navigate(`/users/${record.id}`),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 960 }}
      />

      <Modal
        title={`为 ${topupUser?.nickname || topupUser?.openId || `用户 ${topupUser?.id}`} 调整虚拟币余额`}
        open={!!topupUser}
        onOk={onTopup}
        confirmLoading={topupLoading}
        onCancel={() => {
          setTopupUser(null);
          form.resetFields();
        }}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          当前余额：{(topupUser?.coinBalance ?? 0).toLocaleString()} 虚拟币 · 正数=充值，负数=扣减
        </Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item
            label="变动数量（虚拟币）"
            name="coins"
            rules={[
              { required: true, message: '请输入数量' },
              {
                validator: (_, value) =>
                  typeof value === 'number' && value !== 0
                    ? Promise.resolve()
                    : Promise.reject(new Error('数量不能为 0')),
              },
            ]}
          >
            <InputNumber
              step={100}
              min={-1000000}
              max={1000000}
              style={{ width: '100%' }}
              placeholder="例如 1000 充值，-100 扣减"
            />
          </Form.Item>
          <Form.Item label="备注" name="note">
            <Input.TextArea rows={2} placeholder="给账目记录留个原因，便于事后对账" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserListPage;
